import { defineTokens } from '@pandacss/dev';
import { toPandaToken } from "../toPandaToken";

/**
 * Single source of truth for the color palette.
 *
 * A flat mapping of `colorPath -> value` (no `{ value }` wrapper, no nested objects).
 * Entries within a palette are ordered lightest -> darkest. Values are the raw CSS color,
 * or a Panda token reference (`{colors.x.y}`) for semantic aliases.
 *
 * `colorsTokens` (Panda `defineTokens` format) is generated from this mapping via
 * `toPandaToken`, so the token paths (`colors.blue.main`, …) stay identical to before.
 * Runtime code that needs a raw color value resolves it from this same mapping instead of
 * the `token()` helper — that lets the `remove-unused-css-vars` Panda hook safely prune
 * variables only referenced at runtime.
 */
export const colorValues = {
  transparent: 'transparent',
  white: '#ffffff',
  black: '#000',

  // Blue Palette (light -> dark)
  'blue.lightest': '#EEF7FF',
  'blue.extraLight': '#DEEFFF',
  'blue.lighter': '#9DCDFA',
  'blue.light': '#54A0E8',
  'blue.main': '#005FB9',
  'blue.medium': '#0048B8',
  'blue.dark': '#02427E',
  'blue.darkest': '#02143A',

  // Green Palette (light -> dark)
  'green.light': '#E3FAF2',
  'green.lightest': '#E3FAF2',
  'green.extraLight': '#EFFCF8',
  'green.lighter': '#B7ECDA',
  'green.disabled': '#80C1A9',
  'green.main': '#008254',
  'green.darker': '#267F4C',
  'green.dark2': '#00682C',
  'green.dark': '#006642',
  'green.darkest': '#00482F',

  // Yellow Palette (light -> dark)
  'yellow.extraLight': '#FFFBF4',
  'yellow.lightest': '#FFF7EA',
  'yellow.lighter2': '#FFF0D6',
  'yellow.lighter': '#FFE2AE',
  'yellow.light': '#FBD083',
  'yellow.main': '#FFC764',
  'yellow.dark': '#EFA92E',
  'yellow.darkest': '#B07000',

  // Red Palette (light -> dark)
  'red.lightest': '#FFE8E8',
  'red.lighter2': '#FFBFBF',
  'red.lighter': '#FF9090',
  'red.light': '#FF5D5E',
  'red.main': '#DE2828',
  'red.dark': '#B40000',

  // Brown Palette (light -> dark)
  'brown.extraLight': '#FFFAF9',
  'brown.lightest': '#FEF4F1',
  'brown.lighter2': '#FFEAE2',
  'brown.lighter': '#EDBEAD',
  'brown.light': '#D58B70',
  'brown.main': '#B96C4F',
  'brown.dark': '#91472B',
  'brown.darkest': '#652812',

  // Neutral Palette (light -> dark)
  'neutral.white': '#FFFFFF',
  'neutral.lightest': '#F5F5F5',
  'neutral.lighter2': '#EBEBEC',
  'neutral.lighter': '#D8D9DA',
  'neutral.light': '#B1B3B5',
  'neutral.main': '#8B8C8F',
  'neutral.dark': '#64666A',
  'neutral.darker': '#3D4045',
  'neutral.darkest': '#2A2A2A',
  'neutral.black': '#000000',

  // NerdWallet Green accents (light -> dark)
  'nwGreen.snowyMint': '#DDFED9',
  'nwGreen.mossGreen': '#B7E1B2',
  'nwGreen.easterGreen': '#97CB65',
  'nwGreen.tintLP': '#A8EA4C',
  'nwGreen.light': '#40AD87',
  'nwGreen.lightOcean': '#81D6B8',

  // Lime accents (light -> dark)
  'lime.lightest': '#F8FFE4',
  'lime.tintLP2': '#E8FDAC',
  'lime.tintLP': '#DDFA88',
  'lime.main': '#E2FF39',

  // NerdWallet Logo Green (light -> dark)
  'nwLogoGreen.light': '#8DC63F',
  'nwLogoGreen.main': '#00AE4D',
  'nwLogoGreen.dark': '#00682C',

  // Highlight Accents
  'highlightAccent.pineapple': '#FFFF66',
  'highlightAccent.lime': '#E2FF39',
  'highlightAccent.pink': '#FF97E9',
  'highlightAccent.lilac': '#EBD5EF',
  'highlightAccent.bone': '#F4EEE6',
  'highlightAccent.cobalt': '#1900FF',

  // Semantic Colors (references to the palette above)
  primary: '{colors.green.main}',
  'primary-dark': '{colors.green.dark}',
  'primary-light': '{colors.nwGreen.light}',
  secondary: '{colors.blue.main}',
  'secondary-light': '{colors.blue.lightest}',
  error: '{colors.red.main}',
  'error-dark': '{colors.red.dark}',
  'error-light': '{colors.red.light}',
  background: '{colors.neutral.white}',
} as const;

export const colorsTokens = defineTokens({
  colors: toPandaToken<string>(colorValues),
});

const colorValuesMap = colorValues as Record<string, string>;

/**
 * Resolves a color token path (e.g. `neutral.dark`, `primary`, or the fully-qualified
 * `colors.neutral.dark`) to its raw CSS value, following semantic `{colors.x.y}` references.
 * Returns the input unchanged when it is not a known token path, so callers may pass raw CSS
 * colors through. Use this at runtime instead of the `token()` helper.
 */
export function resolveColorValue(colorToken: string): string {
  const key = colorToken.startsWith('colors.') ? colorToken.slice('colors.'.length) : colorToken;
  const value = colorValuesMap[key];
  if (value === undefined) return colorToken;
  if (value.startsWith('{colors.')) {
    return resolveColorValue(value.slice(1, -1));
  }
  return value;
}
